import React, { useState, useEffect, useRef } from 'react';
import { 
    Box, Typography, Switch, TextField, IconButton, FormControlLabel, 
    Paper, Divider, Avatar, InputAdornment, CircularProgress 
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack'; // 🔥 NUEVO ICONO

import { getAiMessages, toggleConversationStatus, sendManualMessage } from '../../../../controllers/company/ai/AiChatController';
import { formatWhatsAppMessage } from '../../../../controllers/AuxController';

export default function ChatWindow({ chat, isMobile, onBack }) {
    const [isLoading, setIsLoading] = useState(false);
    const [messages, setMessages] = useState([]);
    const [isHumanActive, setIsHumanActive] = useState(chat.status === 'human_active');
    const [inputValue, setInputValue] = useState('');
    const [messageSearch, setMessageSearch] = useState('');
    const [isSending, setIsSending] = useState(false);

    const messagesEndRef = useRef(null);

    // 🔥 CAMBIO 1: Solo reseteamos los inputs cuando cambiamos a un cliente DIFERENTE (chat?.id)
    useEffect(() => {
        setIsHumanActive(chat.status === 'human_active');
        setMessageSearch(''); 
        setInputValue('');
    }, [chat?.id]); // <-- Antes decía [chat]

    // 🔥 CAMBIO 2: Solo hacemos el scroll inicial y montamos el polling cuando entramos a un cliente nuevo
    useEffect(() => {
        if (!chat?.id) return;
        
        const initChat = async () => {
            await fetchMessages(false);
            setTimeout(() => {
                scrollToBottom();
            }, 150);
        };
        
        initChat();

        const intervalId = setInterval(() => fetchMessages(true), 5000);
        
        return () => clearInterval(intervalId);
    }, [chat?.id]); // <-- Antes decía [chat]

    /*useEffect(() => {
        scrollToBottom();
    }, [messages]);*/

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    };

    const fetchMessages = async (isBackground = false) => {
        if (!isBackground) setIsLoading(true);
        try {
            const response = await getAiMessages(chat.id);
            if (response && response.data) {
                setMessages(response.data.messages || response.data);
            }
        } catch (error) {
            console.error("Error cargando los mensajes:", error);
        } finally {
            if (!isBackground) setIsLoading(false);
        }
    };

    const changeStatus = async () => {
        try {
            await toggleConversationStatus(chat.id);
        } catch (error) {
            console.error("Error actualizando estado:", error);
        }
    };

    const handleSendMessage = async () => {
        const messageText = inputValue.trim();
        if (!messageText || !isHumanActive || isSending) return;

        setIsSending(true);
        setInputValue('');

        try {
            await sendManualMessage(chat.id, messageText);
            await fetchMessages(true);
            scrollToBottom();
        } catch (error) {
            console.error("Error enviando mensaje manual:", error);
            setInputValue(messageText);
        } finally {
            setIsSending(false);
        }
    };

    const filteredMessages = messages.filter(msg => 
        msg.content && msg.content.toLowerCase().includes(messageSearch.toLowerCase())
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
            
            {/* --- CABECERA MINIMALISTA --- */}
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e0e0e0', bgcolor: '#fff', gap: 1 }}>
                
                <Box display="flex" alignItems="center" gap={1.5}>
                    {/* Botón atrás solo visible en teléfonos */}
                    {isMobile && (
                        <IconButton onClick={onBack} sx={{ color: '#555', mr: -0.5 }}>
                            <ArrowBackIcon />
                        </IconButton>
                    )}
                    
                    <Avatar sx={{ bgcolor: isHumanActive ? '#fff3e0' : '#e3f2fd', color: isHumanActive ? '#f57c00' : 'primary.main', width: 40, height: 40 }}>
                        {isHumanActive ? <PersonIcon fontSize="small" /> : <SmartToyIcon fontSize="small" />}
                    </Avatar>
                    
                    <Box>
                        <Typography variant="subtitle2" fontWeight="800" color="#111" noWrap sx={{ maxWidth: isMobile ? 120 : 200 }}>
                            {chat.display_name || chat.phone}
                        </Typography>
                        <Typography variant="caption" color={isHumanActive ? 'warning.main' : 'primary.main'} fontWeight="700">
                            {isHumanActive ? 'Control Manual' : 'IA Activa'}
                        </Typography>
                    </Box>
                </Box>

                {/* Switch de Control Humano (Abreviado en móviles) */}
                <FormControlLabel
                    sx={{ m: 0 }}
                    control={
                        <Switch 
                            size="small"
                            checked={isHumanActive} 
                            onChange={(e) => {
                                setIsHumanActive(e.target.checked);
                                changeStatus();
                            }}
                            color="warning" 
                        />
                    }
                    label={
                        !isMobile && (
                            <Typography variant="caption" fontWeight="700" color="text.secondary" ml={0.5}>
                                {isHumanActive ? 'Pausar' : 'Intervenir'}
                            </Typography>
                        )
                    }
                    labelPlacement="start"
                />
            </Box>

            {/* --- ÁREA DE MENSAJES --- */}
            <Box sx={{ flexGrow: 1, p: { xs: 2, sm: 3 }, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                {isLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                        <CircularProgress size={28} />
                    </Box>
                ) : (
                    <>
                        {filteredMessages.map((msg) => {
                            const isUser = msg.role === 'user';
                            const isModel = msg.role === 'model';
                        
                            return (
                                <Box key={msg.id} sx={{ display: 'flex', flexDirection: 'column', alignItems: isUser ? 'flex-start' : 'flex-end', width: '100%' }}>
                                    <Paper 
                                        elevation={0}
                                        sx={{ 
                                            p: 1.5, px: 2,
                                            maxWidth: { xs: '85%', sm: '75%' },
                                            // 🔥 Volvemos al verde suave (#e8f5e9) para el modelo/Agente
                                            bgcolor: isUser ? '#fff' : (isModel ? '#e8f5e9' : '#fff3e0'), 
                                            border: '1px solid',
                                            // 🔥 Le regresamos el borde verde sutil (#c8e6c9)
                                            borderColor: isUser ? '#e0e0e0' : (isModel ? '#c8e6c9' : '#ffe0b2'),
                                            borderRadius: 3,
                                            borderBottomLeftRadius: isUser ? 0 : 3,
                                            borderBottomRightRadius: !isUser ? 0 : 3,
                                        }}
                                    >
                                        <Typography 
                                            variant="body2" 
                                            color="#222"
                                            dangerouslySetInnerHTML={{ __html: formatWhatsAppMessage(msg.content) }} 
                                            sx={{ 
                                                wordBreak: 'break-word',
                                                whiteSpace: 'pre-wrap', 
                                                '& p': { m: 0, padding: 0 } // 🔥 CRÍTICO: Elimina los espacios gigantes que meten las etiquetas <p> de HTML
                                            }}
                                        />
                                    </Paper>
                                    <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, px: 1, fontWeight: '500' }}>
                                        {msg.created_at 
                                            ? new Date(msg.created_at).toLocaleString('es-VE', { 
                                                day: '2-digit', 
                                                month: 'short', 
                                                hour: '2-digit', 
                                                minute: '2-digit', 
                                                hour12: true 
                                              }) 
                                            : (msg.time || '')}
                                    </Typography>
                                </Box>
                            );
                        })}
                        <div ref={messagesEndRef} />
                    </>
                )}
            </Box>

            {/* --- BARRA DE ENVÍO Y BÚSQUEDA --- */}
            <Box sx={{ p: 1.5, bgcolor: '#fff', borderTop: '1px solid #e0e0e0' }}>
                {/* Ocultamos el buscador en móviles si la IA está activa para dejarle todo el espacio al input */}
                {(!isMobile || isHumanActive) && (
                    <Box display="flex" gap={1}>
                        {!isMobile && (
                            <TextField
                                size="small"
                                placeholder="Buscar en chat..."
                                value={messageSearch}
                                onChange={(e) => setMessageSearch(e.target.value)}
                                sx={{ width: 140, '& .MuiOutlinedInput-root': { borderRadius: 8, bgcolor: '#f9fafb' } }}
                            />
                        )}
                        <TextField
                            fullWidth size="small"
                            placeholder={isHumanActive ? "Mensaje al cliente..." : "Activa el switch para intervenir"}
                            disabled={!isHumanActive || isSending}
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSendMessage();
                                }
                            }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 8, bgcolor: isHumanActive ? '#fff' : '#f5f5f5' } }}
                        />
                        <IconButton 
                            color="primary" 
                            disabled={!isHumanActive || !inputValue.trim() || isSending}
                            onClick={handleSendMessage}
                            sx={{ 
                                bgcolor: (isHumanActive && inputValue.trim() && !isSending) ? 'primary.main' : '#f0f0f0',
                                color: (isHumanActive && inputValue.trim() && !isSending) ? '#fff' : '#ccc',
                                '&:hover': { bgcolor: 'primary.dark' },
                            }}
                        >
                            {isSending ? <CircularProgress size={20} color="inherit" /> : <SendIcon fontSize="small" sx={{ ml: 0.5 }} />}
                        </IconButton>
                    </Box>
                )}
            </Box>

        </Box>
    );
}